import {isUndefined} from 'lodash';
import React, {FC} from 'react';

import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme, useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {buildCalendarEventTimeText} from '../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {TimeFormatsEnum} from '../../helpers/dateTimeHelpers';
import {maybeBuildRecurrenceText} from '../../helpers/models/calendarEventHelpers';
import {CalendarEventModel} from '../../models/api/calendarEventModel';
import {CalendarEventVersionModel} from '../../models/api/calendarEventVersionModel';
import {ChannelModel} from '../../models/api/channelModel';
import {ParticipantTypesEnum} from '../../models/api/participantModel';
import {VisualParticipantModel} from '../../models/api/visualParticipantModel';

const campaignUrl =
  'https://frontapp.com?utm_source=calendar&utm_medium=email&utm_campaign=calendar_event_email_attendees';
const repeatIconUrl = 'https://frontapp.com/extra/mails/calendar/repeat.png';
const locationIconUrl = 'https://frontapp.com/extra/mails/calendar/location.png';
const teammatesIconUrl = 'https://frontapp.com/extra/mails/calendar/teammates.png';

/*
 * Props.
 */

export interface CalendarEventEmailProps {
  channel: ChannelModel;
  calendarEvent: CalendarEventModel;
  calendarEventVersion: CalendarEventVersionModel;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  timeFormat: TimeFormatsEnum;
}

interface CalendarEventDetailRowProps {
  iconUrl: string;
  content: string;
}

/**
 * Style.
 * Note: styles are being inlined because this component will be converted to an HTML
 * string using ReactDOMServer#renderToStaticMarkup and then appended to an email.
 */

const emailContainerStyle = {
  width: '100%',
  margin: 0,
  padding: 0,
  fontFamily: 'Helvetica Neue,Helvetica,Segoe UI,Verdana,Arial,sans-serif',
};

const tableStyle = {
  width: '100%',
};

const makeCalendarEventDetailTableStyle = (theme: UserTheme) => ({
  ...tableStyle,
  backgroundColor: theme.greys.white,
  border: '1px solid #ddd',
  borderRadius: '16px',
  borderSpacing: '0',
  overflow: 'hidden',
  margin: '0',
});

const makeCallToActionTextContainerStyle = (theme: UserTheme) => ({
  fontSize: fontSizes.verySmall,
  fontWeight: fontWeights.bold,
  color: theme.greys.shade60,
});

const makeAnchorStyle = (theme: UserTheme) => ({
  color: theme.palette[PaletteColorsEnum.BLUE].shade40,
});

const emptyRowStyle = {
  height: '20px',
};

const emptyCellStyle = {
  width: '8px',
  fontSize: '1px',
};

const fontFamily = 'Helvetica Neue,Helvetica,Segoe UI,Verdana,Arial,sans-serif';

const makeTitleStyle = (theme: UserTheme) => ({
  fontFamily,
  fontSize: fontSizes.large,
  fontWeight: fontWeights.bold,
  color: theme.greys.shade90,
});

const textContainerStyle = {
  fontFamily,
  fontSize: fontSizes.small,
  fontWeight: fontWeights.normal,
};

const iconContainerStyle = {
  width: '25px',
  verticalAlign: 'top',
};

/*
 *  Component.
 */

export const CalendarEventEmail: FC<CalendarEventEmailProps> = (props) => {
  const theme = useUserTheme();
  return (
    <>
      {/* Two line breaks at the start to provide space for the user's email content */}
      <br />
      <br />
      <div style={emailContainerStyle}>
        {renderCalendarEventDetailTable(props, theme)}
        {renderCallToAction(props.channel, theme)}
      </div>
      {/* One line break at the end so that, if the initial composer caret position
          is set to the end of this html, it starts on a newline */}
      <br />
    </>
  );
};

const CalendarEventDetailRow: FC<CalendarEventDetailRowProps> = (props) => (
  <tr>
    <td style={iconContainerStyle}>
      <img width={16} height={16} src={props.iconUrl} alt="" />
    </td>
    <td>
      <span style={textContainerStyle}>{props.content}</span>
    </td>
  </tr>
);

const EmptyRow = () => (
  <tr style={emptyRowStyle}>
    <td style={emptyCellStyle}>&nbsp;</td>
  </tr>
);

function renderCalendarEventDetailTable(props: CalendarEventEmailProps, theme: UserTheme) {
  const {calendarEvent, calendarEventVersion, visualParticipants, timeFormat} = props;

  return (
    <table style={makeCalendarEventDetailTableStyle(theme)} cellSpacing={0} cellPadding={14}>
      <tbody>
        <tr>
          <td>
            <table style={tableStyle}>
              <tbody>
                <tr>
                  <td>
                    <span style={makeTitleStyle(theme)}>{calendarEventVersion.summary}</span>
                  </td>
                </tr>
                {renderCalendarEventTimeRow(calendarEventVersion, timeFormat)}
                <EmptyRow />
              </tbody>
            </table>
            <table style={tableStyle}>
              <tbody>
                {maybeRenderRecurrenceRow(calendarEvent)}
                {maybeRenderLocationsRow(calendarEventVersion)}
                {renderParticipantsRow(visualParticipants)}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function renderCalendarEventTimeRow(
  calendarEventVersion: CalendarEventVersionModel,
  timeFormat: TimeFormatsEnum,
) {
  const calendarEventTimeText = buildCalendarEventTimeText(calendarEventVersion, timeFormat);
  return (
    <tr>
      <td>
        <span style={textContainerStyle}>{calendarEventTimeText}</span>
      </td>
    </tr>
  );
}

function maybeRenderRecurrenceRow(calendarEvent: CalendarEventModel) {
  const recurrenceText = maybeBuildRecurrenceText(calendarEvent.recurrence);
  if (isUndefined(recurrenceText)) {
    return null;
  }

  return <CalendarEventDetailRow iconUrl={repeatIconUrl} content={recurrenceText} />;
}

function maybeRenderLocationsRow(calendarEventVersion: CalendarEventVersionModel) {
  if (!calendarEventVersion.locations.length) {
    return null;
  }

  const locationsContent = calendarEventVersion.locations.map((l) => l.displayName).join(', ');
  return <CalendarEventDetailRow iconUrl={locationIconUrl} content={locationsContent} />;
}

function renderParticipantsRow(visualParticipants: ReadonlyArray<VisualParticipantModel>) {
  return (
    <CalendarEventDetailRow iconUrl={teammatesIconUrl} content={getParticipantsText(visualParticipants)} />
  );
}

function renderCallToAction(channel: ChannelModel, theme: UserTheme) {
  const senderEmail = channel.displayName;
  return (
    <table cellPadding={10}>
      <tbody>
        <tr>
          <td>
            <span style={makeCallToActionTextContainerStyle(theme)}>
              {senderEmail} is using{' '}
              <a style={makeAnchorStyle(theme)} href={campaignUrl}>
                {' '}
                Front
              </a>
              , the communication hub for building strong customer relationships.
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

const COLLAPSE_PARTICIPANTS_LIMIT = 20;
const MAX_PARTICIPANTS_TO_SHOW = 16;
function getParticipantsText(visualParticipants: ReadonlyArray<VisualParticipantModel>) {
  const participantNames = visualParticipants
    .filter((p) => p.type !== ParticipantTypesEnum.RESOURCE) // don't include resources like meeting rooms
    .map((p) => {
      if (p.isOrganizer) {
        return `${p.contact.handle} (organizer)`;
      }

      return p.contact.handle;
    });

  if (participantNames.length < COLLAPSE_PARTICIPANTS_LIMIT) {
    return participantNames.join(', ');
  }

  // If there are more than COLLAPSE_PARTICIPANTS_LIMIT,
  // show a subset and collapse the rest into "+ X more"
  const collpasedParticipants = participantNames.slice(0, MAX_PARTICIPANTS_TO_SHOW);
  return `${collpasedParticipants.join(', ')} + ${participantNames.length - MAX_PARTICIPANTS_TO_SHOW} more`;
}
